import * as React from 'react'

import { Box, Button, type Icon } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import {
  CustomResourcesResourceValuesInput,
  CustomTableAttachValuesDocument,
  EditCustomTablePageResourceValueFragment,
  EditCustomTablePageSchemaFieldValueFragment,
  EditCustomTablePageSchemaFieldValueFragmentDoc,
  EmployeeCustomTablesResourceValueFragment,
  EmployeeCustomTablesResourceValueFragmentDoc,
  EmployeeCustomTablesSchemaFragment,
  EmployeeCustomTablesSchemaFragmentDoc,
  EmployeeCustomTablesSchemaResourceFragment,
  EmployeeCustomTablesSchemaResourceFragmentDoc,
  NewCustomTablePageSchemaFragment,
} from 'generated/resources/graphql'
import { keyBy, sortBy } from 'lodash'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { InputItem } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/InputItem'
import { createForm } from 'modules/employees/components/CustomResources/SchemaComponent/Form/utils/createForm'
import { RouteContext } from 'modules/employees/pages/CustomTablesListPage/context'

interface Props {
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']
  title?: string
  description?: string
  employeeId: number
  schema?: NewCustomTablePageSchemaFragment | null
  resourceValue?: EditCustomTablePageResourceValueFragment | null
  currency: string
  canRemoveCustomTableRecords: boolean
  onClose: () => void
}

const FormModal = ({
  illustration = 'table-file',
  title = i18n.t('custom_tables.modal.title'),
  description = i18n.t('custom_tables.modal.description'),
  employeeId,
  schema,
  resourceValue,
  currency,
  canRemoveCustomTableRecords,
  onClose,
}: Props) => {
  const rootRoute = React.useContext(RouteContext)
  const customValues: EditCustomTablePageSchemaFieldValueFragment[] =
    resourceValue?.customValues ?? []
  const customValuesByFieldId = keyBy(customValues, 'fieldId')
  const resourceId = schema?.resourcesConnection?.nodes[0]?.id
  const resourceFields = schema?.fields ?? []
  const sortedResourceFields = sortBy(resourceFields, 'position')

  const forms = React.useRef(
    sortedResourceFields.reduce<Form[]>((acc, field) => {
      const customField = field.field
      if (customField && schema) {
        acc.push(
          createForm({
            schema,
            field: customField,
            value: customValuesByFieldId[customField.id],
          })
        )
      }
      return acc
    }, [])
  ).current

  const [updateValue] = useMutation(CustomTableAttachValuesDocument, {
    extractorFn: (data) => data.customResourcesMutations.attachValuesCustomresourcesResource,
    update: (cache, { data }, { variables }) => {
      const mutationData = data?.customResourcesMutations?.attachValuesCustomresourcesResource
      if (!mutationData?.resource) return

      const schemaId = variables?.schemaId
      const mutationEmployeeId = variables?.employeeId
      const mutationResourceId = mutationData.resource.id
      const responseResourceValues = mutationData.resource.resourceValues ?? []

      if (!schemaId || !mutationEmployeeId || responseResourceValues.length === 0) return

      // Step 1: Check if CustomResourcesResource exists in cache
      let cachedResource = cache.readFragment<EmployeeCustomTablesSchemaResourceFragment>({
        id: `CustomResourcesResource:${mutationResourceId}`,
        fragment: EmployeeCustomTablesSchemaResourceFragmentDoc,
        fragmentName: 'EmployeeCustomTablesSchemaResource',
      })

      // Step 2: If resource doesn't exist, create it and attach it to the schema
      if (!cachedResource) {
        // Read schema from cache
        const cachedSchema = cache.readFragment<EmployeeCustomTablesSchemaFragment>({
          id: `CustomResourcesSchema:${schemaId}`,
          fragment: EmployeeCustomTablesSchemaFragmentDoc,
          fragmentName: 'EmployeeCustomTablesSchema',
          variables: { employeeId: mutationEmployeeId },
        })

        if (cachedSchema) {
          // Create new resource
          const newResource: EmployeeCustomTablesSchemaResourceFragment = {
            __typename: 'CustomResourcesResource',
            id: mutationResourceId,
            resourceValues: [],
          }

          // Write the resource to cache
          cache.writeFragment({
            id: `CustomResourcesResource:${mutationResourceId}`,
            fragment: EmployeeCustomTablesSchemaResourceFragmentDoc,
            fragmentName: 'EmployeeCustomTablesSchemaResource',
            data: newResource,
          })

          // Update schema's resourcesConnection to include the new resource
          const updatedNodes = [...(cachedSchema.resourcesConnection?.nodes ?? []), newResource]

          cache.writeFragment({
            id: `CustomResourcesSchema:${schemaId}`,
            fragment: EmployeeCustomTablesSchemaFragmentDoc,
            fragmentName: 'EmployeeCustomTablesSchema',
            data: {
              ...cachedSchema,
              resourcesConnection: {
                ...cachedSchema.resourcesConnection,
                nodes: updatedNodes,
              },
            },
            variables: { employeeId: mutationEmployeeId },
          })

          cachedResource = newResource
        }
      }

      if (!cachedResource) return

      // Step 3 & 4: Process each resourceValue from the mutation response
      responseResourceValues.forEach((responseResourceValue) => {
        if (!responseResourceValue?.id) return

        const resourceValueId = responseResourceValue.id
        const responseCustomValues = responseResourceValue.customValues ?? []

        // Write each CustomFieldsValue to cache
        responseCustomValues.forEach((customValue) => {
          if (!customValue.id) return

          const customFieldsValueData: EditCustomTablePageSchemaFieldValueFragment = {
            __typename: 'CustomFieldsValue',
            id: customValue.id,
            fieldId: customValue.fieldId,
            booleanValue: customValue.booleanValue ?? null,
            centsValue: customValue.centsValue ?? null,
            dateValue: customValue.dateValue ?? null,
            longTextValue: customValue.longTextValue ?? null,
            multipleChoiceValue: customValue.multipleChoiceValue ?? null,
            numberValue: customValue.numberValue ?? null,
            singleChoiceValue: customValue.singleChoiceValue ?? null,
          }

          cache.writeFragment({
            id: `CustomFieldsValue:${customValue.id}`,
            fragment: EditCustomTablePageSchemaFieldValueFragmentDoc,
            fragmentName: 'EditCustomTablePageSchemaFieldValue',
            data: customFieldsValueData,
          })
        })

        // Find if resourceValue exists in the resource's resourceValues array
        const existingResourceValue = cachedResource.resourceValues.find(
          (rv) => rv.id === resourceValueId
        )

        // Step 4: Create CustomResourcesValue if it doesn't exist
        if (!existingResourceValue) {
          const newResourceValue: EmployeeCustomTablesResourceValueFragment = {
            __typename: 'CustomResourcesValue',
            id: resourceValueId,
            resourceId: mutationResourceId,
            customValues: responseCustomValues.map((cv) => ({
              __typename: 'CustomFieldsValue' as const,
              id: cv.id,
              fieldId: cv.fieldId,
              booleanValue: cv.booleanValue ?? null,
              centsValue: cv.centsValue ?? null,
              dateValue: cv.dateValue ?? null,
              fieldType: cv.fieldType ?? null,
              longTextValue: cv.longTextValue ?? null,
              multipleChoiceValue: cv.multipleChoiceValue ?? null,
              numberValue: cv.numberValue ?? null,
              singleChoiceValue: cv.singleChoiceValue ?? null,
            })),
          }

          cache.writeFragment({
            id: `CustomResourcesValue:${resourceValueId}`,
            fragment: EmployeeCustomTablesResourceValueFragmentDoc,
            fragmentName: 'EmployeeCustomTablesResourceValue',
            data: newResourceValue,
          })
        }

        // Step 5: Replace the customValues list with the mutation response
        const updatedCustomValues = responseCustomValues.map((cv) => ({
          __typename: 'CustomFieldsValue' as const,
          id: cv.id,
          fieldId: cv.fieldId,
          booleanValue: cv.booleanValue ?? null,
          centsValue: cv.centsValue ?? null,
          dateValue: cv.dateValue ?? null,
          fieldType: cv.fieldType ?? null,
          longTextValue: cv.longTextValue ?? null,
          multipleChoiceValue: cv.multipleChoiceValue ?? null,
          numberValue: cv.numberValue ?? null,
          singleChoiceValue: cv.singleChoiceValue ?? null,
        }))

        // Update the CustomResourcesValue with the new customValues
        cache.writeFragment({
          id: `CustomResourcesValue:${resourceValueId}`,
          fragment: EmployeeCustomTablesResourceValueFragmentDoc,
          fragmentName: 'EmployeeCustomTablesResourceValue',
          data: {
            __typename: 'CustomResourcesValue',
            id: resourceValueId,
            resourceId: mutationResourceId,
            customValues: updatedCustomValues,
          },
        })

        // Update the resource's resourceValues array
        const updatedResourceValues: EmployeeCustomTablesResourceValueFragment[] =
          existingResourceValue
            ? cachedResource.resourceValues.map((rv) =>
                rv.id === resourceValueId
                  ? {
                      __typename: 'CustomResourcesValue' as const,
                      id: resourceValueId,
                      resourceId: mutationResourceId,
                      customValues: updatedCustomValues,
                    }
                  : rv
              )
            : [
                ...cachedResource.resourceValues,
                {
                  __typename: 'CustomResourcesValue' as const,
                  id: resourceValueId,
                  resourceId: mutationResourceId,
                  customValues: updatedCustomValues,
                },
              ]

        cache.writeFragment({
          id: `CustomResourcesResource:${mutationResourceId}`,
          fragment: EmployeeCustomTablesSchemaResourceFragmentDoc,
          fragmentName: 'EmployeeCustomTablesSchemaResource',
          data: {
            ...cachedResource,
            resourceValues: updatedResourceValues,
          },
        })
      })
    },
  })

  const getStringValue = (fieldType: string, value?: string | number | null): string => {
    if (!value) return ''
    const stringValue = value.toString()

    if (fieldType === 'date') {
      const uiDate = stringToDate(stringValue, 'ui')
      return i18nDateToString(uiDate, 'backend')
    }

    return stringValue
  }

  const performAction = async () => {
    const values: CustomResourcesResourceValuesInput[] = forms
      .filter((form) => form.get('editable').value && form.isDirty)
      .map((form) => {
        const valueId = form.get('value_id').value ? Number(form.get('value_id').value) : null
        const fieldType = form.get('field_type').value
        const formValue = form.get('value').value
        return {
          fieldId: Number(form.get('field_id').value),
          valueId: valueId,
          value: getStringValue(fieldType, formValue),
        }
      })

    const result = await updateValue({
      variables: {
        employeeId,
        resourceId: resourceId,
        schemaId: schema?.id ?? 0,
        valueId: resourceValue?.id,
        valueIds: resourceValue?.id ? [resourceValue.id] : null,
        values,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('custom_tables.edit_modal.success'),
      })
      onClose()
    }
  }

  const submitEnabled = forms.some((f) => f.isDirty)

  const footer = (
    <Box flexDirection="row" justifyContent="flexEnd" gap="s8" background="grey100">
      {!!resourceValue && canRemoveCustomTableRecords && rootRoute && schema && (
        <Button.Router
          to={rootRoute.customTablesDelete(String(schema.id), String(resourceValue.id))}
          type="negative"
        >
          {i18n.t('custom_tables.modal.delete')}
        </Button.Router>
      )}

      <Button disabled={!submitEnabled} submit>
        {i18n.t('custom_tables.modal.submit')}
      </Button>
    </Box>
  )

  return (
    <Modal
      illustration={illustration}
      title={title}
      description={description}
      onSubmit={performAction}
      onClose={onClose}
      footer={footer}
    >
      {forms.map((form: Form, key: number) => (
        <FormRow key={key}>
          <InputItem form={form} currency={currency} />
        </FormRow>
      ))}
    </Modal>
  )
}

export default observer(FormModal)
